import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { useAuth } from '../../../context/AuthContext';
import bridgeService from '../../../services/bridgeService';
import useNotify from '../../../hooks/useNotify';
import shmSensorService from '../services/shmSensorService';
import useSensorSocket, { SENSOR_DATA_EVENT } from '../hooks/useSensorSocket';
import { filterBridgesByRole } from '../../../utils/bridgeAccess';

const SensorContext = createContext(null);

const initialState = {
  latestSensorData: {},
  bridgeNames: {},
  selectedBridge: null,
  selectedSensor: null,
  socketConnected: false,
  loading: true,
  resensys: null,
};

const normalizeSocketId = (value) => {
  if (value == null || value === '') return '';
  if (typeof value === 'object' && value._id != null) return String(value._id);
  return String(value);
};

const mergeReading = (existing, reading) => ({
  ...existing,
  ...reading,
  bridgeId: normalizeSocketId(reading.bridgeId || existing?.bridgeId),
  sensorId: normalizeSocketId(reading.sensorId || existing?.sensorId),
});

function sensorReducer(state, action) {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_BRIDGE_NAMES':
      return { ...state, bridgeNames: action.payload };
    case 'SET_LIVE_DATA': {
      const latestSensorData = {};
      (action.payload.data || []).forEach((reading) => {
        const key = normalizeSocketId(reading.sensorId || reading._id);
        if (key) latestSensorData[key] = mergeReading(null, reading);
      });
      return {
        ...state,
        latestSensorData,
        resensys: action.payload.resensys || state.resensys,
        loading: false,
      };
    }
    case 'PATCH_READING': {
      const key = normalizeSocketId(action.payload.sensorId);
      if (!key) return state;
      return {
        ...state,
        latestSensorData: {
          ...state.latestSensorData,
          [key]: mergeReading(state.latestSensorData[key], action.payload),
        },
      };
    }
    case 'SET_SELECTED_BRIDGE':
      return { ...state, selectedBridge: action.payload };
    case 'SET_SELECTED_SENSOR':
      return { ...state, selectedSensor: action.payload };
    case 'SET_SOCKET_CONNECTED':
      return { ...state, socketConnected: action.payload };
    default:
      return state;
  }
}

export function SensorProvider({ children }) {
  const [state, dispatch] = useReducer(sensorReducer, initialState);
  const { token, user } = useAuth();
  const { showToast } = useNotify();

  const loadBridges = useCallback(async () => {
    try {
      const data = await bridgeService.getAll();
      const names = {};
      filterBridgesByRole(data.bridges || [], user).forEach((bridge) => {
        names[bridge._id] = bridge.name;
      });
      dispatch({ type: 'SET_BRIDGE_NAMES', payload: names });
    } catch (e) {
      showToast(e.message, 'error');
    }
  }, [showToast, user]);

  const loadLiveData = useCallback(async () => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await shmSensorService.getLive();
      dispatch({ type: 'SET_LIVE_DATA', payload: response });
    } catch (e) {
      dispatch({ type: 'SET_LOADING', payload: false });
      showToast(e.message, 'error');
    }
  }, [showToast]);

  const handleSocketEvent = useCallback((event) => {
    if (event.type === 'connected') {
      dispatch({ type: 'SET_SOCKET_CONNECTED', payload: true });
      return;
    }
    if (event.type === 'disconnected') {
      dispatch({ type: 'SET_SOCKET_CONNECTED', payload: false });
      return;
    }
    if (event.type === SENSOR_DATA_EVENT && event.reading) {
      dispatch({ type: 'PATCH_READING', payload: event.reading });
    }
  }, []);

  useSensorSocket(handleSocketEvent);

  useEffect(() => {
    if (!token) {
      dispatch({ type: 'SET_LOADING', payload: false });
      return;
    }
    loadBridges();
    loadLiveData();
  }, [token, loadBridges, loadLiveData]);

  const setSelectedBridge = useCallback((bridgeId) => {
    dispatch({ type: 'SET_SELECTED_BRIDGE', payload: bridgeId });
  }, []);

  const setSelectedSensor = useCallback((sensorId) => {
    dispatch({ type: 'SET_SELECTED_SENSOR', payload: sensorId });
  }, []);

  const getBridgeName = useCallback(
    (bridgeId) => state.bridgeNames[bridgeId] || 'Unknown Bridge',
    [state.bridgeNames]
  );

  const sensorList = useMemo(
    () => Object.values(state.latestSensorData),
    [state.latestSensorData]
  );

  const filteredSensors = useMemo(() => {
    if (!state.selectedBridge) return sensorList;
    const bridgeKey = normalizeSocketId(state.selectedBridge);
    return sensorList.filter((s) => normalizeSocketId(s.bridgeId) === bridgeKey);
  }, [sensorList, state.selectedBridge]);

  const selectedSensorData = useMemo(() => {
    if (!state.selectedSensor) return null;
    return state.latestSensorData[state.selectedSensor] || null;
  }, [state.latestSensorData, state.selectedSensor]);

  const value = useMemo(
    () => ({
      ...state,
      sensorList,
      filteredSensors,
      selectedSensorData,
      setSelectedBridge,
      setSelectedSensor,
      getBridgeName,
      loadLiveData,
      refreshBridges: loadBridges,
    }),
    [
      state,
      sensorList,
      filteredSensors,
      selectedSensorData,
      setSelectedBridge,
      setSelectedSensor,
      getBridgeName,
      loadLiveData,
      loadBridges,
    ]
  );

  return (
    <SensorContext.Provider value={value}>{children}</SensorContext.Provider>
  );
}

export function useSensors() {
  const context = useContext(SensorContext);
  if (!context) {
    throw new Error('useSensors must be used within SensorProvider');
  }
  return context;
}
